import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FileText, Upload, Download, Building2, Menu, X } from 'lucide-react'
import * as XLSX from 'xlsx'
import JSZip from 'jszip'
import GstSidebar from '../../components/dashboard/GstSidebar'
import ExcelDataGrid from '../../components/common/ExcelDataGrid'
import { generateGstr7Json } from '../../utils/gstr7Helpers'
import { useError } from '../../contexts/ErrorContext'
import { getStorageItem, setStorageItem } from '../../utils/storage'
import { useClient } from '../../contexts/ClientContext'

console.log('DEBUG: ExcelDataGrid Import:', ExcelDataGrid);

const Gstr7Form = () => {
  const navigate = useNavigate()
  const { addError } = useError()
  const { activeClient } = useClient()

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [sidebarState, setSidebarState] = useState({
    isOpen: false,
    isExpanded: false,
    keepOpen: false,
    hovered: false
  })

  // Form data
  const [gstr7Data, setGstr7Data] = useState([])
  const [isDataLoaded, setIsDataLoaded] = useState(false)

  // Moved loadData logic below selectedYear initialization

  const [activeTab, setActiveTab] = useState('data-entry')

  // Calculate Summary Data automatically
  const summaryData = React.useMemo(() => {
    if (gstr7Data.length === 0) return []
    const grouped = gstr7Data.reduce((acc, row) => {
      const master = row.gstinMaster || 'UNKNOWN'
      if (!acc[master]) {
        acc[master] = {
          gstinMaster: master,
          records: [],
          totalTaxable: 0,
          totalValue: 0,
          totalIgst: 0,
          totalCgst: 0,
          totalSgst: 0,
          vendorCount: new Set()
        }
      }
      acc[master].records.push(row)
      acc[master].totalTaxable += parseFloat(row.taxableValue || 0)
      acc[master].totalValue += parseFloat(row.invoiceValue || 0)
      acc[master].totalIgst += parseFloat(row.igst || 0)
      acc[master].totalCgst += parseFloat(row.cgst || 0)
      acc[master].totalSgst += parseFloat(row.sgst || 0)
      acc[master].vendorCount.add(row.gstinVendor)
      return acc
    }, {})
    return Object.values(grouped)
  }, [gstr7Data])

  // Initialize with valid default values
  const getValidDefaultValues = () => {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1

    // If current year is 2025 and we're before September, default to September
    if (currentYear === 2025 && currentMonth < 9) {
      return { year: 2025, month: 9 }
    }

    // If current year is 2025 and we're September or later, use current month
    if (currentYear === 2025 && currentMonth >= 9) {
      return { year: 2025, month: currentMonth }
    }

    // For years after 2025, use current month (but not beyond current month)
    if (currentYear > 2025) {
      return { year: currentYear, month: currentMonth }
    }

    // Fallback to September 2025
    return { year: 2025, month: 9 }
  }

  const defaultValues = getValidDefaultValues()
  const [selectedYear, setSelectedYear] = useState(() => {
    const savedYear = localStorage.getItem('gstYear')
    return savedYear ? parseInt(savedYear) : defaultValues.year
  })
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const savedMonth = localStorage.getItem('gstMonth')
    return savedMonth ? parseInt(savedMonth) : defaultValues.month
  })

  // Dynamic Key Generator
  const getStorageKey = React.useCallback(() => {
    const clientId = activeClient ? activeClient._id : 'guest'
    return `gstr7Data_${clientId}_${selectedYear}_${selectedMonth}`
  }, [activeClient, selectedYear, selectedMonth])

  const [dataKey, setDataKey] = useState(null);

  // Load data asynchronously
  useEffect(() => {
    const key = getStorageKey();
    let isCurrent = true;
    const loadData = async () => {
      try {
        const savedData = await getStorageItem(key)
        if (isCurrent) {
          if (savedData) {
            setGstr7Data(Array.isArray(savedData) ? savedData : [])
          } else {
            setGstr7Data([])
          }
          setDataKey(key)
          setIsDataLoaded(true)
        }
      } catch (error) {
        console.error('Failed to load GSTR7 data', error)
        if (isCurrent) {
          setDataKey(key)
          setIsDataLoaded(true)
        }
      }
    }
    loadData()
    return () => { isCurrent = false; }
  }, [getStorageKey])

  // Save data to new storage whenever it changes (only after initial load)
  useEffect(() => {
    if (isDataLoaded && dataKey === getStorageKey()) {
      setStorageItem(getStorageKey(), gstr7Data)
    }
  }, [gstr7Data, dataKey, isDataLoaded, getStorageKey])

  // Manual entry state
  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [newRow, setNewRow] = useState({
    gstinMaster: activeClient?.gstinTds || '',
    gstinVendor: '',
    invoiceNo: '',
    invoiceDate: '',
    invoiceValue: '',
    taxableValue: '',
    igst: '',
    cgst: '',
    sgst: '',
    action: 'N'
  })

  // Check if mobile view and validate stored values
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    // Validate stored year and month values - kept from original
    const year = selectedYear
    const month = selectedMonth
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1

    let newYear = year
    let newMonth = month
    let changed = false

    // Validate year (must be 2025+)
    if (year < 2025) {
      newYear = 2025
      changed = true
    }

    // Validate month based on year
    if (newYear === 2025 && month < 9) {
      newMonth = 9
      changed = true
    } else if (newYear === currentYear && month > currentMonth) {
      newMonth = currentMonth
      changed = true
    }

    if (changed) {
      setSelectedYear(newYear)
      setSelectedMonth(newMonth)
      localStorage.setItem('gstYear', newYear.toString())
      localStorage.setItem('gstMonth', newMonth.toString())
    }

    return () => window.removeEventListener('resize', checkMobile)
  }, []) // Dependency array empty is fine as we read initial state

  // No specific "Load data" useEffect needed anymore as we lazy load

  // Helper function to round to 2 decimal places
  const roundToTwoDecimals = (value) => {
    if (value === null || value === undefined || value === '') return 0
    const num = parseFloat(value)
    if (isNaN(num)) return 0
    return Math.round(num * 100) / 100
  }

  // Row Validation Helper
  const validateRowData = (row) => {
    const errors = []

    // Invoice Validation
    const invoiceNo = (row.invoiceNo || '').toString()
    if (invoiceNo.length > 16) {
      errors.push(`Invoice No. "${invoiceNo}" exceeds 16 characters`)
    }
    if (invoiceNo.includes('\\')) { // Check for backslash
      errors.push(`Invoice No. "${invoiceNo}" cannot contain "\\" character`)
    }
    // Existing check for spaces (already handled by replace, but good to check)
    if (invoiceNo.includes(' ')) {
      errors.push(`Invoice No. "${invoiceNo}" cannot contain spaces`)
    }

    // GSTIN Validation
    if (row.gstinMaster && row.gstinVendor && row.gstinMaster.trim().toUpperCase() === row.gstinVendor.trim().toUpperCase()) {
      errors.push(`GSTIN (Master) and GSTIN (Vendor) cannot be the same: ${row.gstinMaster}`)
    }

    return errors
  }

  // Calculate Totals
  const calculateTotals = () => {
    return gstr7Data.reduce((acc, row) => ({
      invoiceValue: acc.invoiceValue + parseFloat(row.invoiceValue || 0),
      taxableValue: acc.taxableValue + parseFloat(row.taxableValue || 0),
      igst: acc.igst + parseFloat(row.igst || 0),
      cgst: acc.cgst + parseFloat(row.cgst || 0),
      sgst: acc.sgst + parseFloat(row.sgst || 0),
      count: acc.count + 1
    }), { invoiceValue: 0, taxableValue: 0, igst: 0, cgst: 0, sgst: 0, count: 0 })
  }
  const totals = calculateTotals()

  // Save data
  const saveData = (data) => {
    setGstr7Data(data)
    if (isDataLoaded) {
      setStorageItem('gstr7Data', data)
    }
  }

  // Handle sidebar state changes
  const handleSidebarStateChange = (newState) => {
    setSidebarState(newState)
  }

  const handleSidebarClose = () => {
    setSidebarOpen(false)
  }

  // Year and month options - Restricted to 2025+ and September+
  const yearOptions = Array.from({ length: 10 }, (_, i) => 2025 + i)
  const allMonthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ]

  // Excel upload handler
  const handleExcelUpload = (event) => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[sheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        // Expected headers
        const expectedHeaders = [
          'GSTIN (Master)', 'GSTIN (Vendor)', 'Invoice No.', 'Invoice Date',
          'Invoice Value', 'Taxable Value', 'IGST', 'CGST', 'SGST'
        ]

        // Validate headers
        const headers = Object.keys(jsonData[0] || {})
        const missingHeaders = expectedHeaders.filter(header => !headers.includes(header))

        if (missingHeaders.length > 0) {
          addError(`Missing headers: ${missingHeaders.join(', ')}`)
          return
        }

        // Helper function to convert Excel date serial number to date string
        const convertExcelDate = (excelDate) => {
          if (!excelDate) return ''

          // If it's already a string, return as is
          if (typeof excelDate === 'string') return excelDate

          // If it's a number (Excel serial date), convert it
          if (typeof excelDate === 'number') {
            // Excel date serial number (days since 1900-01-01, but Excel has a bug with leap year 1900)
            // Excel counts 1900 as a leap year, but it wasn't, so we need to adjust
            const excelEpoch = new Date(1899, 11, 30) // December 30, 1899
            const date = new Date(excelEpoch.getTime() + excelDate * 24 * 60 * 60 * 1000)

            // Format as DD-MM-YYYY
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}-${month}-${year}`
          }

          return excelDate.toString()
        }

        // Process data with validation
        const processedData = []
        const errors = []

        jsonData.forEach((row, index) => {
          const rowNumber = index + 2 // Excel row number (1-indexed, +1 for header)

          // Get raw values
          const invoiceNoRaw = row['Invoice No.'] || ''
          const invoiceValueRaw = row['Invoice Value']
          const taxableValueRaw = row['Taxable Value']
          const igstRaw = row['IGST'] || 0
          const cgstRaw = row['CGST'] || 0
          const sgstRaw = row['SGST'] || 0

          // Check for spaces in Invoice No.
          if (invoiceNoRaw && typeof invoiceNoRaw === 'string' && invoiceNoRaw.includes(' ')) {
            errors.push(`Row ${rowNumber}: Invoice No. cannot contain spaces`)
            return
          }

          // Check for NIL/empty Invoice Value
          if (invoiceValueRaw === null || invoiceValueRaw === undefined || invoiceValueRaw === '' ||
            invoiceValueRaw === 'NIL' || invoiceValueRaw === 'nil' || invoiceValueRaw === 'Nil') {
            errors.push(`Row ${rowNumber}: Invoice Value cannot be empty or NIL`)
            return
          }

          // Check for NIL/empty Taxable Value
          if (taxableValueRaw === null || taxableValueRaw === undefined || taxableValueRaw === '' ||
            taxableValueRaw === 'NIL' || taxableValueRaw === 'nil' || taxableValueRaw === 'Nil') {
            errors.push(`Row ${rowNumber}: Taxable Value cannot be empty or NIL`)
            return
          }

          // Convert to numbers
          const invoiceValue = roundToTwoDecimals(invoiceValueRaw)
          const taxableValue = roundToTwoDecimals(taxableValueRaw)
          const igst = roundToTwoDecimals(igstRaw)
          const cgst = roundToTwoDecimals(cgstRaw)
          const sgst = roundToTwoDecimals(sgstRaw)

          // Check for negative values
          if (invoiceValue < 0) {
            errors.push(`Row ${rowNumber}: Invoice Value cannot be negative`)
            return
          }
          if (taxableValue < 0) {
            errors.push(`Row ${rowNumber}: Taxable Value cannot be negative`)
            return
          }
          if (igst < 0) {
            errors.push(`Row ${rowNumber}: IGST cannot be negative`)
            return
          }
          if (cgst < 0) {
            errors.push(`Row ${rowNumber}: CGST cannot be negative`)
            return
          }
          if (sgst < 0) {
            errors.push(`Row ${rowNumber}: SGST cannot be negative`)
            return
          }

          // All validations passed, add to processed data
          const invoiceNo = (invoiceNoRaw || '').toString().replace(/\s+/g, '')

          processedData.push({
            id: Date.now() + index,
            gstinMaster: row['GSTIN (Master)'] || '',
            gstinVendor: row['GSTIN (Vendor)'] || '',
            invoiceNo: invoiceNo,
            invoiceDate: convertExcelDate(row['Invoice Date']),
            invoiceValue: invoiceValue,
            taxableValue: taxableValue,
            igst: igst,
            cgst: cgst,
            sgst: sgst,
            action: 'N' // Default to Add
          })

          // Validate the new row using common validator
          const rowErrors = validateRowData(processedData[processedData.length - 1])
          if (rowErrors.length > 0) {
            errors.push(`Row ${rowNumber}: ${rowErrors.join(', ')}`)
            processedData.pop() // Remove invalid row
            return
          }
        })

        // Show errors if any
        if (errors.length > 0) {
          addError(`Validation errors found: ${errors.join('; ')}`)
          if (processedData.length > 0) {
            // Save valid rows if any
            saveData(processedData)
            toast.info(`Only ${processedData.length} valid row(s) were imported. Please fix errors and re-upload.`)
          }
          return
        }

        saveData(processedData)
        toast.success('Excel file uploaded successfully!')
      } catch (error) {
        console.error('Error processing Excel file:', error)
        addError('Error processing Excel file. Please check the format.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  // Excel download handler
  const handleExcelDownload = () => {
    const formatDateForExcel = (dateStr) => {
      if (!dateStr) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
      }
      return dateStr;
    };

    try {
      const worksheet = XLSX.utils.json_to_sheet(gstr7Data.map(row => ({
        'GSTIN (Master)': row.gstinMaster,
        'GSTIN (Vendor)': row.gstinVendor,
        'Invoice No.': row.invoiceNo,
        'Invoice Date': formatDateForExcel(row.invoiceDate),
        'Invoice Value': row.invoiceValue,
        'Taxable Value': row.taxableValue,
        'IGST': row.igst,
        'CGST': row.cgst,
        'SGST': row.sgst
      })))

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GSTR7 Data')
      XLSX.writeFile(workbook, `GSTR7_Data_${selectedYear}_${selectedMonth}.xlsx`)
      toast.success('Excel file downloaded successfully!')
    } catch (error) {
      console.error('Error downloading Excel file:', error)
      addError('Error downloading Excel file.')
    }
  }

  // Template download handler
  const handleTemplateDownload = () => {
    try {
      const templateData = [
        {
          'GSTIN (Master)': '07AACCE4248H1DZ',
          'GSTIN (Vendor)': '04AACCN0199E1ZR',
          'Invoice No.': '2024-25/115',
          'Invoice Date': '17-03-2025',
          'Invoice Value': roundToTwoDecimals(74977.20),
          'Taxable Value': roundToTwoDecimals(63540.00),
          'IGST': roundToTwoDecimals(1270.80),
          'CGST': roundToTwoDecimals(0.00),
          'SGST': roundToTwoDecimals(0.00)
        }
      ]

      const worksheet = XLSX.utils.json_to_sheet(templateData)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'GSTR7 Template')
      XLSX.writeFile(workbook, 'GSTR7_Template.xlsx')
      toast.success('Template downloaded successfully!')
    } catch (error) {
      console.error('Error downloading template:', error)
      addError('Error downloading template.')
    }
  }

  // Generate Single JSON
  const generateJSON = (gstinMaster, records) => {
    // Use unified helper
    const jsonData = generateGstr7Json(gstinMaster, records, selectedMonth, selectedYear)
    return JSON.stringify(jsonData, null, 2)
  }

  const handleDownloadSingleJSON = (summaryInfo) => {
    try {
      const jsonString = generateJSON(summaryInfo.gstinMaster, summaryInfo.records)
      const blob = new Blob([jsonString], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `GSTR7_${summaryInfo.gstinMaster}_${selectedMonth}_${selectedYear}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success(`JSON for ${summaryInfo.gstinMaster} downloaded`)
    } catch (e) {
      console.error(e)
      addError('Failed to download JSON')
    }
  }

  const handleDownloadZip = async () => {
    try {
      const zip = new JSZip()
      summaryData.forEach(item => {
        const jsonString = generateJSON(item.gstinMaster, item.records)
        zip.file(`GSTR7_${item.gstinMaster}_${selectedMonth}_${selectedYear}.json`, jsonString)
      })

      const content = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(content)
      const link = document.createElement('a')
      link.href = url
      link.download = `GSTR7_All_Files_${selectedMonth}_${selectedYear}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('All files downloaded as ZIP!')
    } catch (e) {
      console.error(e)
      addError('Failed to create ZIP file')
    }
  }

  // Update action
  const updateAction = (id, action) => {
    const updatedData = gstr7Data.map(row =>
      row.id === id ? { ...row, action } : row
    )
    saveData(updatedData)
  }

  // Delete row
  const deleteRow = (id) => {
    const updatedData = gstr7Data.filter(row => row.id !== id)
    saveData(updatedData)
    toast.success('Row deleted successfully!')
  }

  // Clear all data
  const clearAllData = () => {
    if (gstr7Data.length === 0) {
      toast.info('No data to clear.')
      return
    }

    if (window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      saveData([])
      toast.success('All data cleared successfully!')
    }
  }

  // Manual entry functions
  const handleAddNew = () => {
    setIsAdding(true)
    setEditingId(null)
    setNewRow({
      gstinMaster: '',
      gstinVendor: '',
      invoiceNo: '',
      invoiceDate: '',
      invoiceValue: '',
      taxableValue: '',
      igst: '',
      cgst: '',
      sgst: '',
      action: 'N'
    })
  }

  const handleSaveRow = () => {
    if (!newRow.gstinMaster || !newRow.gstinVendor || !newRow.invoiceNo) {
      addError('Please fill in all required fields')
      return
    }

    // Check for spaces in Invoice No.
    if (newRow.invoiceNo && newRow.invoiceNo.includes(' ')) {
      addError('Invoice No. cannot contain spaces')
      return
    }

    // Validate Invoice Value - cannot be empty or NIL
    if (!newRow.invoiceValue || newRow.invoiceValue === '' ||
      newRow.invoiceValue === 'NIL' || newRow.invoiceValue === 'nil' || newRow.invoiceValue === 'Nil') {
      addError('Invoice Value cannot be empty or NIL')
      return
    }

    // Validate Taxable Value - cannot be empty or NIL
    if (!newRow.taxableValue || newRow.taxableValue === '' ||
      newRow.taxableValue === 'NIL' || newRow.taxableValue === 'nil' || newRow.taxableValue === 'Nil') {
      addError('Taxable Value cannot be empty or NIL')
      return
    }

    // Convert and validate numeric values
    const invoiceValue = roundToTwoDecimals(newRow.invoiceValue)
    const taxableValue = roundToTwoDecimals(newRow.taxableValue)
    const igst = roundToTwoDecimals(newRow.igst || 0)
    const cgst = roundToTwoDecimals(newRow.cgst || 0)
    const sgst = roundToTwoDecimals(newRow.sgst || 0)

    // Check for negative values
    if (invoiceValue < 0) {
      addError('Invoice Value cannot be negative')
      return
    }
    if (taxableValue < 0) {
      addError('Taxable Value cannot be negative')
      return
    }
    if (igst < 0) {
      addError('IGST cannot be negative')
      return
    }
    if (cgst < 0) {
      addError('CGST cannot be negative')
      return
    }
    if (sgst < 0) {
      addError('SGST cannot be negative')
      return
    }

    // Remove any spaces from invoice number
    const invoiceNo = (newRow.invoiceNo || '').replace(/\s+/g, '')

    // Create candidate row for validation
    const candidateRow = {
      ...newRow,
      invoiceNo,
      invoiceValue, // numbers
      taxableValue,
      igst,
      cgst,
      sgst
    }

    // Common Validation
    const commonErrors = validateRowData(candidateRow)
    if (commonErrors.length > 0) {
      addError(commonErrors[0])
      return
    }

    const rowData = {
      ...candidateRow,
      id: editingId || Date.now(),
      action: newRow.action
    }

    if (editingId) {
      const updatedData = gstr7Data.map(row => row.id === editingId ? rowData : row)
      saveData(updatedData)
      toast.success('Row updated successfully')
    } else {
      const updatedData = [...gstr7Data, rowData]
      saveData(updatedData)
      toast.success('Row added successfully')
    }

    setIsAdding(false)
    setEditingId(null)
    setNewRow({
      gstinMaster: activeClient?.gstinTds || '',
      gstinVendor: '',
      invoiceNo: '',
      invoiceDate: '',
      invoiceValue: '',
      taxableValue: '',
      igst: '',
      cgst: '',
      sgst: '',
      action: 'N'
    })
  }

  const handleEditRow = (row) => {
    setEditingId(row.id)
    setIsAdding(true)
    setNewRow({
      gstinMaster: row.gstinMaster,
      gstinVendor: row.gstinVendor,
      invoiceNo: row.invoiceNo,
      invoiceDate: row.invoiceDate,
      invoiceValue: row.invoiceValue.toString(),
      taxableValue: row.taxableValue.toString(),
      igst: row.igst.toString(),
      cgst: row.cgst.toString(),
      sgst: row.sgst.toString(),
      action: row.action
    })
  }

  const handleCancelEdit = () => {
    setIsAdding(false)
    setEditingId(null)
    setNewRow({
      gstinMaster: activeClient?.gstinTds || '',
      gstinVendor: '',
      invoiceNo: '',
      invoiceDate: '',
      invoiceValue: '',
      taxableValue: '',
      igst: '',
      cgst: '',
      sgst: '',
      action: 'N'
    })
  }

  // Grid Handlers
  const handleGridDataChange = (newData, rowIndex, columnKey) => {
    let updatedRow = { ...newData[rowIndex] }

    // Logic for negative values (already present in checks usually) can be added here

    // Strict Validation on Change
    const validationErrors = validateRowData(updatedRow)
    if (validationErrors.length > 0) {
      addError(validationErrors[0])

      // Revert logic for strict Invoice No compliance
      const oldRow = gstr7Data[rowIndex]
      if (columnKey === 'invoiceNo') {
        const inv = updatedRow.invoiceNo
        if (inv.length > 16 || inv.includes('\\')) {
          updatedRow.invoiceNo = oldRow.invoiceNo
        }
      }
    }

    const newGridData = [...newData]
    newGridData[rowIndex] = updatedRow
    setGstr7Data(newGridData)
    saveData(newGridData)
  }

  const handleGridRowAdd = () => {
    const newRecord = {
      id: Date.now(),
      gstinMaster: activeClient?.gstinTds || '',
      gstinVendor: '',
      invoiceNo: '',
      invoiceDate: '',
      invoiceValue: '',
      taxableValue: '',
      igst: '',
      cgst: '',
      sgst: '',
      action: 'N'
    }
    const updatedData = [...gstr7Data, newRecord]
    setGstr7Data(updatedData)
    saveData(updatedData)
  }

  const handleGridRowDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this row?')) {
      const updatedData = gstr7Data.filter(row => row.id !== id)
      setGstr7Data(updatedData)
      saveData(updatedData)
    }
  }


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center space-x-2">
              <Building2 className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">GST Return</h1>
                <p className="text-sm text-gray-500">GSTR7 - TDS on GST</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                localStorage.removeItem('gstr7Data')
                localStorage.removeItem('gstYear')
                localStorage.removeItem('gstMonth')
                localStorage.removeItem('SELECTED_RETURN_TYPE')
                navigate('/')
              }}
              className="flex items-center text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex w-full">
        {/* Sidebar */}
        <GstSidebar
          onSidebarStateChange={(state) => setSidebarOpen(state.isOpen)}
          onNavigationAttempt={(path) => navigate(path)}
        />

        {/* Main Content */}
        <main className="flex-1 ml-64 w-full min-w-0">
          <div className="p-2 w-full max-w-none">
            {/* Page Title */}
            <div className="mb-2">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                GSTR7 - TDS on GST
              </h2>
              <p className="text-gray-600 text-xs">
                Manage your GSTR7 return details for {allMonthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}.
              </p>
              <p className="text-xs text-blue-600 mt-1">
                <strong>Note:</strong> GSTR7 returns are available for September 2025 onwards. Future months are not selectable.
              </p>
            </div>

            {/* Tab Navigation */}
            <div className="mb-4">
              <div className="bg-white p-1 rounded-lg shadow-sm border border-gray-200 w-fit">
                <nav className="flex space-x-1" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('data-entry')}
                    className={`px-6 py-2 rounded-md font-medium text-sm transition-all duration-200 ${activeTab === 'data-entry'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    Data Entry
                  </button>
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`px-6 py-2 rounded-md font-medium text-sm transition-all duration-200 ${activeTab === 'summary'
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                  >
                    Summary
                  </button>
                </nav>
              </div>
            </div>

            {/* Year and Month Selection */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Financial Year
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => {
                      const newYear = parseInt(e.target.value)
                      const currentDate = new Date()
                      const currentYear = currentDate.getFullYear()
                      const currentMonth = currentDate.getMonth() + 1

                      setSelectedYear(newYear)
                      localStorage.setItem('gstYear', e.target.value)

                      // If year is 2025, ensure month is September or later
                      if (newYear === 2025 && selectedMonth < 9) {
                        setSelectedMonth(9)
                        localStorage.setItem('gstMonth', '9')
                      }

                      // If year is current year, ensure month doesn't exceed current month
                      if (newYear === currentYear && selectedMonth > currentMonth) {
                        setSelectedMonth(currentMonth)
                        localStorage.setItem('gstMonth', currentMonth.toString())
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Month
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => {
                      const newMonth = parseInt(e.target.value)
                      setSelectedMonth(newMonth)
                      localStorage.setItem('gstMonth', e.target.value)
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {allMonthOptions
                      .filter(month => {
                        const currentDate = new Date()
                        const currentYear = currentDate.getFullYear()
                        const currentMonth = currentDate.getMonth() + 1

                        // For 2025, only show September onwards
                        if (selectedYear === 2025) {
                          return month.value >= 9
                        }

                        // For current year, don't show months beyond current month
                        if (selectedYear === currentYear) {
                          return month.value <= currentMonth
                        }

                        // For years after current year, show all months
                        return true
                      })
                      .map(month => (
                        <option key={month.value} value={month.value}>{month.label}</option>
                      ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'data-entry' && (
              <>
            {/* Excel Data Grid View */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200 flex justify-end space-x-2">
                <button
                  onClick={() => document.getElementById('excel-upload').click()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  Upload Excel
                </button>
                <button
                  onClick={handleTemplateDownload}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  Download Template
                </button>
                <button
                  onClick={handleExcelDownload}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm"
                >
                  Download Excel
                </button>
                <button
                  onClick={clearAllData}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                >
                  Clear All
                </button>
                <button
                  onClick={handleGridRowAdd}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                >
                  Add New Row
                </button>
                <button
                  onClick={() => setActiveTab('summary')}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                >
                  Generate Return
                </button>
              </div>

              <ExcelDataGrid
                columns={[
                  { key: 'gstinMaster', label: 'GSTIN (Master)', type: 'text', width: '150px', required: true, disabled: !!activeClient },
                  { key: 'gstinVendor', label: 'GSTIN (Vendor)', type: 'text', width: '150px', required: true },
                  { key: 'invoiceNo', label: 'Invoice No', type: 'text', width: '150px', required: true },
                  { key: 'invoiceDate', label: 'Invoice Date', type: 'text', width: '120px', placeholder: 'YYYY-MM-DD' },
                  { key: 'invoiceValue', label: 'Inv Value', type: 'number', width: '120px', required: true },
                  { key: 'taxableValue', label: 'Taxable Val', type: 'number', width: '120px', required: true },
                  { key: 'igst', label: 'IGST', type: 'number', width: '100px' },
                  { key: 'cgst', label: 'CGST', type: 'number', width: '100px' },
                  { key: 'sgst', label: 'SGST', type: 'number', width: '100px' },
                  { key: 'action', label: 'Action', type: 'select', width: '100px', options: [{ value: 'N', label: 'Add' }, { value: 'D', label: 'Delete' }] },
                ]}
                data={gstr7Data}
                onDataChange={handleGridDataChange}
                onRowAdd={handleGridRowAdd}
                onRowDelete={handleGridRowDelete}
                height="calc(100vh - 340px)"
                stickyFirstColumn={true}
                summaryRow={gstr7Data.length > 0 ? {
                  gstinMaster: <span className="text-gray-500 font-bold">TOTALS</span>,
                  invoiceValue: <span className="text-blue-600 font-bold">₹{roundToTwoDecimals(totals.invoiceValue)}</span>,
                  taxableValue: <span className="text-green-600 font-bold">₹{roundToTwoDecimals(totals.taxableValue)}</span>,
                  igst: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(totals.igst)}</span>,
                  cgst: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(totals.cgst)}</span>,
                  sgst: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(totals.sgst)}</span>,
                } : null}
              />


              {gstr7Data.length === 0 && (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No data entries yet. Use the grid above or "Upload Excel" to add data.
                </div>
              )}
            </div>
              </>
            )}

            {activeTab === 'summary' && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="mb-6 flex justify-between items-center border-b border-gray-200 pb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-purple-600" />
                      Return Summary & Download
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Review the summary grouped by GSTIN (Master) before downloading JSON files.
                    </p>
                  </div>
                  {summaryData.length > 0 && (
                    <button
                      onClick={handleDownloadZip}
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center text-sm shadow-sm"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download All (ZIP)
                    </button>
                  )}
                </div>

                {summaryData.length === 0 ? (
                  <div className="text-center text-gray-500 py-8 text-sm">
                    No data available. Please add data in the Data Entry tab to generate the summary.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {summaryData.map((item, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-lg text-gray-800">{item.gstinMaster}</span>
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                {item.records.length} Invoices
                              </span>
                              <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full">
                                {item.vendorCount.size} Vendors
                              </span>
                            </div>
                            <div className="mt-3 text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-2 border-t border-gray-100 pt-3">
                              <div>Total Taxable: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(item.totalTaxable)}</span></div>
                              <div>Total Invoice: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(item.totalValue)}</span></div>
                              <div>IGST: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(item.totalIgst)}</span></div>
                              <div>CGST: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(item.totalCgst)}</span></div>
                              <div>SGST: <span className="font-medium text-gray-900">₹{roundToTwoDecimals(item.totalSgst)}</span></div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownloadSingleJSON(item)}
                            className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 text-sm font-medium flex items-center transition-colors shadow-sm"
                          >
                            <Download className="w-3 h-3 mr-2" />
                            JSON
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Hidden file input */}
      <input
        id="excel-upload"
        type="file"
        accept=".xlsx,.xls"
        onChange={handleExcelUpload}
        className="hidden"
      />

      <ToastContainer />
    </div>
  )
}

export default Gstr7Form
